/**
 * iter633 — Allergens management (Foodics-style).
 */
import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { SettingsPageHeader } from '@/components/settings/SettingsPageHeader';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Wheat, Plus, Trash2, Upload as UploadIcon } from 'lucide-react';
import { Textarea } from '../components/ui/textarea';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function AllergensPage() {
  const [allergens, setAllergens] = useState([]);
  const [presets, setPresets] = useState([]);
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('');
  const [saving, setSaving] = useState(false);
  const [bulkCsv, setBulkCsv] = useState('');
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  // iter654 — Click-through products dialog state.
  const [productsFor, setProductsFor] = useState(null);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsList, setProductsList] = useState([]);
  const openProductsFor = async (allergen) => {
    setProductsFor(allergen);
    setProductsLoading(true);
    setProductsList([]);
    try {
      const { data } = await api.get(`/allergens/${allergen.id}/products`);
      setProductsList(data.products || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load products');
    } finally {
      setProductsLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  const load = async () => {
    try {
      const { data } = await api.get('/allergens');
      setAllergens((data.allergens || []).filter(a => !a.deleted));
      setPresets(data.presets || []);
    } catch { toast.error('Failed to load'); }
  };

  const add = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await api.post('/allergens', { name, icon });
      setName(''); setIcon('');
      load();
      toast.success('Added');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm('Delete this allergen?')) return;
    try {
      await api.delete(`/allergens/${id}`);
      load();
      toast.success('Deleted');
    } catch { toast.error('Failed'); }
  };

  // iter634 — Bulk allergen tagger. Paste a CSV of "Product, allergen1, allergen2".
  const bulkTag = async () => {
    if (!bulkCsv.trim()) { toast.error('Paste at least one row first'); return; }
    setBulkRunning(true);
    try {
      const { data } = await api.post('/allergens/bulk-tag', { csv: bulkCsv });
      setBulkResult(data);
      toast.success(`Tagged ${data.matched} product(s)`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBulkRunning(false); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4" data-testid="allergens-page">
        <SettingsPageHeader
          icon={Wheat}
          title="Allergens"
          subtitle="Tag products and modifiers with allergens so customers can see what's safe to eat."
          accent="amber"
        />

        <Card data-testid="add-allergen-card">
          <CardContent className="p-4 space-y-2">
            <div className="text-sm font-semibold">Add a new allergen</div>
            <div className="flex gap-2 flex-wrap">
              <Input placeholder="Icon (emoji)" value={icon} onChange={(e) => setIcon(e.target.value)} maxLength={4}
                     className="w-24 text-center text-lg" data-testid="allergen-icon-input" />
              <Input placeholder="Name (e.g., peanuts)" value={name} onChange={(e) => setName(e.target.value)}
                     className="flex-1 min-w-[200px]" data-testid="allergen-name-input" />
              <Button onClick={add} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="allergen-add-btn">
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </div>
            {presets.length > 0 && (
              <div className="text-[11px] text-muted-foreground">
                Quick add:&nbsp;
                {presets.slice(0, 10).map(p => (
                  <button key={p} onClick={() => setName(p)} className="text-indigo-600 hover:underline mr-2" data-testid={`preset-${p}`}>
                    {p}
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="allergens-table">
                <thead>
                  <tr className="bg-stone-50 border-b">
                    <th className="text-left p-3">Allergen</th>
                    <th className="text-right p-3">Products</th>
                    <th className="text-right p-3">Modifier Options</th>
                    <th className="text-right p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {allergens.length === 0 && (
                    <tr><td colSpan="4" className="text-center text-muted-foreground italic p-6">No allergens yet. Add one above.</td></tr>
                  )}
                  {allergens.map(a => (
                    <tr key={a.id} className="border-b hover:bg-stone-50" data-testid={`allergen-row-${a.id}`}>
                      <td className="p-3">
                        <span className="text-xl mr-2">{a.icon || '⚠️'}</span>
                        <span className="capitalize">{a.name}</span>
                      </td>
                      <td className="text-right p-3">
                        <button
                          onClick={() => openProductsFor(a)}
                          disabled={(a.products_count || 0) === 0}
                          className={`text-indigo-600 font-semibold hover:underline disabled:text-muted-foreground disabled:cursor-default disabled:no-underline`}
                          title={(a.products_count || 0) === 0 ? 'No products use this allergen yet' : 'See which products are tagged'}
                          data-testid={`allergen-products-link-${a.id}`}
                        >
                          {a.products_count || 0}
                        </button>
                      </td>
                      <td className="text-right p-3 text-indigo-600 font-semibold">{a.modifier_options_count || 0}</td>
                      <td className="text-right p-3">
                        <button onClick={() => remove(a.id)} className="text-rose-600 hover:text-rose-700" data-testid={`allergen-delete-${a.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* iter634 — Bulk allergen tagger */}
        <Card data-testid="bulk-tag-card">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-2">
              <UploadIcon size={16} className="text-indigo-600" />
              <div className="text-sm font-semibold">Bulk-tag products with allergens</div>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Paste one product per line, comma-separated with its allergens.
              The tagger matches by product name (case-insensitive, prefix-fuzzy)
              and stamps the <code>allergens</code> array on each match.
            </p>
            <Textarea
              value={bulkCsv}
              onChange={(e) => setBulkCsv(e.target.value)}
              rows={6}
              placeholder="Pizza Margherita, wheat, dairy&#10;Caesar Salad, dairy, egg&#10;Tom Yum Soup, fish, shell_fish"
              className="font-mono text-[11px]"
              data-testid="bulk-tag-csv-area"
            />
            <div className="flex items-center justify-between gap-2">
              <div className="text-[10px] text-muted-foreground">
                Format: <code>Product Name, allergen1, allergen2, ...</code>
              </div>
              <Button onClick={bulkTag} disabled={bulkRunning}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white text-[11px] h-8"
                      data-testid="bulk-tag-run-btn">
                {bulkRunning ? 'Tagging…' : 'Run bulk tag'}
              </Button>
            </div>
            {bulkResult && (
              <div className="text-[11px] space-y-1 p-2 rounded bg-stone-50 border border-stone-200" data-testid="bulk-tag-result">
                <div className="font-semibold text-emerald-700">✓ Matched {bulkResult.matched} product(s)</div>
                {bulkResult.missed.length > 0 && (
                  <div className="text-rose-700">
                    ✗ Missed: {bulkResult.missed.slice(0, 5).join(', ')}{bulkResult.missed.length > 5 ? `, +${bulkResult.missed.length - 5} more` : ''}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* iter654 — Products-for-allergen dialog */}
      {productsFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setProductsFor(null)} data-testid="allergen-products-dialog-overlay">
          <div className="bg-background rounded-2xl shadow-2xl max-w-lg w-full p-5 max-h-[80dvh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="allergen-products-dialog">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold text-base font-outfit flex items-center gap-2">
                <span className="text-xl">{productsFor.icon || '⚠️'}</span>
                Products tagged "{productsFor.name}"
              </h2>
              <button onClick={() => setProductsFor(null)} className="text-muted-foreground hover:text-foreground text-lg" data-testid="allergen-products-close">×</button>
            </div>
            {productsLoading ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
            ) : productsList.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center italic">No menu items use this allergen yet.</p>
            ) : (
              <>
                <p className="text-xs text-muted-foreground mb-2">
                  {productsList.length} product{productsList.length === 1 ? '' : 's'} will display a customer-facing warning for <strong>{productsFor.name}</strong>.
                </p>
                <ul className="divide-y border rounded-lg" data-testid="allergen-products-list">
                  {productsList.map(p => (
                    <li key={p.id} className="flex items-center justify-between p-2.5 hover:bg-muted/30" data-testid={`product-${p.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{p.name}</p>
                        {p.name_ar && <p className="text-[10px] text-muted-foreground" dir="rtl">{p.name_ar}</p>}
                      </div>
                      <div className="text-right shrink-0 ml-2">
                        <p className="text-sm font-semibold tabular-nums">SAR {(p.price || 0).toFixed(2)}</p>
                        {!p.is_available && <p className="text-[10px] text-rose-600">Hidden</p>}
                      </div>
                    </li>
                  ))}
                </ul>
                <p className="text-[10px] text-muted-foreground mt-3 text-center">
                  Tip: edit any item under <a href="/menu-items" className="text-indigo-600 hover:underline">Menu Items</a> → <em>Details</em> tab to change its allergen tags.
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
